/**
 * Column-count → sizing helpers shared by the three files grids (#21305):
 * `FilesGrid` (v1 legacy switch), `FilesGridNext`, `FilesGridLegacy`, and the
 * presentation-editor `PresentationNext/components/FilesGrid`. These were byte-identical
 * copies in each file; keeping one source of truth avoids drift.
 *
 * `getColumnWidth` takes the MUI `classes` object (so it can return the matching
 * `gridN` class), which is why it lives here rather than in `@paintscout/util`.
 */

/** MUI `withStyles`/`makeStyles` injected classes: a map of style key → generated class name. */
type GridClasses = Record<string, string>

/**
 * The `gridN` print-media rules (`getColumnWidth`'s `columnClass` values) were copy-pasted
 * verbatim into every grid's style object. Spread this into each so a print-width change (or a
 * new column count) only has to happen once.
 */
export const gridPrintStyles = {
  grid2: {
    '@media print': {
      width: '16.666%',
      flexBasis: 'unset'
    }
  },
  grid3: {
    '@media print': {
      width: '25%',
      flexBasis: 'unset'
    }
  },
  grid4: {
    '@media print': {
      width: '33.333%',
      flexBasis: 'unset'
    }
  },
  grid6: {
    '@media print': {
      width: '50%',
      flexBasis: 'unset'
    }
  },
  grid12: {
    '@media print': {
      width: '100%',
      flexBasis: 'unset'
    }
  }
} as const

export function getImageWidth(columns: number): { width: number } {
  switch (columns) {
    case 1:
      return { width: 1200 }
    case 2:
      return { width: 1000 }
    case 3:
      return { width: 800 }
    case 6:
      return { width: 400 }
    default:
      return { width: 600 }
  }
}

export function getColumnWidth(
  columns: number,
  classes: GridClasses
): { columnWidth: 12 | 6 | 4 | 3 | 2; columnClass: string } {
  switch (columns) {
    case 1:
      return { columnWidth: 12 as const, columnClass: classes.grid12 }
    case 2:
      return { columnWidth: 6 as const, columnClass: classes.grid6 }
    case 3:
      return { columnWidth: 4 as const, columnClass: classes.grid4 }
    case 6:
      return { columnWidth: 2 as const, columnClass: classes.grid2 }
    default:
      return { columnWidth: 3 as const, columnClass: classes.grid3 }
  }
}
